import { useMemo, useState, type ReactNode } from 'react';
import { BarChart } from '../charts/BarChart';
import { DonutChart } from '../charts/DonutChart';
import { RankedBars } from '../charts/RankedBars';
import { Card, ChartCard, DataTable, Stat, StatusChip, type Column } from '../components/data';
import { ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { downloadCsv, fileStamp, slugForFile } from '../lib/download';
import { formatNumber, num } from '../lib/format';
import { useHeldResource } from '../lib/hooks';

interface Count {
  label: string;
  count: number;
}
interface TicketRow {
  id: number;
  ticketNumber: string | null;
  title: string | null;
  link: string | null;
  created: string | null;
  completed: string | null;
  status: string | null;
  priority: string | null;
  issue: string | null;
  subIssue: string | null;
  contact: string | null;
}
interface Device {
  id: number;
  name: string;
  type: string | null;
  category: string | null;
  product: string | null;
  serialNumber: string | null;
  location: string | null;
  installDate: string | null;
  warrantyExpiration: string | null;
  warranty: 'covered' | 'expiring' | 'expired' | 'unknown';
  ticketsThisMonth: number;
}
interface Labor {
  label: string;
  hours: number;
  billable: number;
}
interface Payload {
  company: { id: number; name: string; number: string | null };
  month: string;
  from: string;
  to: string;
  summary: {
    opened: number;
    closed: number;
    avgResolveHours: number | null;
    firstResponse: { measured: number; onTime: number; pct: number | null };
    hoursWorked: number;
    invoiced: number;
    invoices: number;
  };
  issues: (Count & { subIssues: Count[] })[];
  byEmployee: { name: string; email: string | null; tickets: number; open: number }[];
  byPriority: Count[];
  bySource: Count[];
  perDay: { day: string; tickets: number; alerts: number }[];
  openTickets: (TicketRow & { ageDays: number | null })[];
  recurring: { issue: string; subIssue: string; count: number }[];
  alerts: { total: number; byType: Count[] };
  security: { weeks: { week: string; label: string; alerts: number }[]; list: (TicketRow & { category: string | null; description: string | null })[] };
  labor: {
    hoursWorked: number;
    billableHours: number;
    notBilledHours: number;
    supportHours: number;
    projectHours: number;
    byTechnician: Labor[];
    byWorkType: Labor[];
  };
  hardware: { list: Device[]; byType: Count[]; warranties: Device[]; unknownWarranty: number; mostTickets: Device[] };
  projects: {
    id: number;
    name: string;
    number: string | null;
    status: string | null;
    lead: string | null;
    start: string | null;
    due: string | null;
    overdue: boolean;
    percentComplete: number | null;
    estimatedHours: number;
    actualHours: number;
  }[];
  invoices: {
    list: { id: number; number: string; date: string | null; due: string | null; paid: string | null; total: number; tax: number }[];
    items: { item: string; lines: number; quantity: number; amount: number }[];
    lines: number;
  };
  notes: string[];
  fetchedUtc: string;
}

const money = (v: number) => formatNumber(v, 'currency');
const hrs = (v: number) => `${num(v, 1)} h`;
const date = (iso: string | null) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const monthLabel = (k: string) => new Date(`${k}-01T00:00:00`).toLocaleDateString([], { month: 'long', year: 'numeric' });

/** The last full month, as YYYY-MM — the month a client report is usually for. */
function lastMonth() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const ticketLink = (t: TicketRow) =>
  t.link ? (
    <a href={t.link} target="_blank" rel="noreferrer">
      {t.ticketNumber ?? t.id}
    </a>
  ) : (
    (t.ticketNumber ?? String(t.id))
  );

const countCols = (label: string): Column<Count>[] => [
  { key: 'label', label },
  { key: 'count', label: 'Tickets', align: 'right' },
];

/** A heading between the report's groups of items. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <h2 className="cr-section">{title}</h2>
      {children}
    </>
  );
}

/**
 * Reports › Client Report — one client's month as the twenty-five things a
 * client wants to see in a review, live from Autotask
 * (routes/reportsClientReport.ts, which lists the twenty-five and where each
 * comes from). Every item is numbered the same on both sides; every chart has
 * a table twin.
 */
export function ClientReportPage() {
  const companies = useHeldResource<{ id: number; name: string }[]>('/reports/client-report/companies');
  const [company, setCompany] = useState<{ id: number; name: string } | null>(null);
  const [search, setSearch] = useState('');
  const [month, setMonth] = useState(lastMonth);
  const [issue, setIssue] = useState<string | null>(null);
  const data = useHeldResource<Payload>(company ? `/reports/client-report?${new URLSearchParams({ companyId: String(company.id), month })}` : null);
  const d = data.data && company && data.data.company.id === company.id && data.data.month === month ? data.data : null;

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = companies.data ?? [];
    return (q ? list.filter((c) => c.name.toLowerCase().includes(q)) : list).slice(0, 60);
  }, [companies.data, search]);

  const drilled = d && issue ? d.issues.find((i) => i.label === issue) : null;

  const exportCsv = () => {
    if (!d) return;
    downloadCsv(`${slugForFile(d.company.name)}_client_report_${d.month}_${fileStamp()}.csv`, [
      ['Client', d.company.name],
      ['Month', d.month],
      [],
      ['Tickets opened', d.summary.opened],
      ['Tickets closed', d.summary.closed],
      ['Average hours to resolve', d.summary.avgResolveHours ?? ''],
      ['First response on time %', d.summary.firstResponse.pct ?? ''],
      ['Labor hours', d.summary.hoursWorked],
      ['Invoiced', d.summary.invoiced],
      [],
      ['Issue type', 'Sub-issue type', 'Tickets'],
      ...d.issues.flatMap((i) => i.subIssues.map((s) => [i.label, s.label, s.count])),
      [],
      ['Employee', 'Email', 'Tickets', 'Still open'],
      ...d.byEmployee.map((e) => [e.name, e.email ?? '', e.tickets, e.open]),
      [],
      ['Alert type', 'Alerts'],
      ...d.alerts.byType.map((a) => [a.label, a.count]),
      [],
      ['Security ticket', 'Created', 'Title', 'Description'],
      ...d.security.list.map((s) => [s.ticketNumber ?? s.id, s.created ?? '', s.title ?? '', s.description ?? '']),
      [],
      ['Technician', 'Hours', 'Billable'],
      ...d.labor.byTechnician.map((l) => [l.label, l.hours, l.billable]),
      [],
      ['Device', 'Type', 'Product', 'Serial', 'Warranty expires', 'Tickets this month'],
      ...d.hardware.list.map((h) => [h.name, h.category ?? h.type ?? '', h.product ?? '', h.serialNumber ?? '', h.warrantyExpiration ?? '', h.ticketsThisMonth]),
      [],
      ['Open project', 'Status', 'Due', '% complete'],
      ...d.projects.map((p) => [p.name, p.status ?? '', p.due ?? '', p.percentComplete ?? '']),
      [],
      ['Invoice item', 'Lines', 'Quantity', 'Amount'],
      ...d.invoices.items.map((i) => [i.item, i.lines, i.quantity, i.amount]),
    ]);
  };

  return (
    <>
      <PageHead
        title="Client Report"
        lede="One client's month as the twenty-five things a client wants to see — tickets, who raised them and what about, alerts and security, labor, hardware, open projects and what was invoiced. Live from Autotask."
      />

      <section className="card mb-4 cr-controls">
        <div className="card-body">
          {company ? (
            <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
              <div className="grow" style={{ flexBasis: 320 }}>
                <span className="eyebrow">Client</span>
                <h2 style={{ margin: 0 }}>{company.name}</h2>
              </div>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCompany(null)}>
                Change client
              </button>
              <Field label="Month">
                <input
                  className="input"
                  type="month"
                  value={month}
                  onChange={(e) => {
                    if (e.target.value) {
                      setMonth(e.target.value);
                      setIssue(null);
                    }
                  }}
                />
              </Field>
              <button type="button" className="btn btn-secondary btn-sm" disabled={!d} onClick={exportCsv}>
                Export CSV
              </button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={!d} onClick={() => window.print()}>
                Print
              </button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
                {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
              </button>
            </div>
          ) : (
            <>
              <span className="eyebrow">Choose a client</span>
              {companies.error && <ErrorBanner message={companies.error} />}
              {companies.loading && <Loading label="Reading active companies from Autotask" />}
              {companies.data && (
                <>
                  <input
                    className="input mt-2"
                    autoFocus
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={`Type to filter ${companies.data.length.toLocaleString()} active companies`}
                  />
                  <div className="sdf-pick mt-2">
                    {matches.length === 0 && <p className="xs muted">No active company contains “{search}”.</p>}
                    {matches.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className="sdf-pick-row"
                        onClick={() => {
                          setCompany(c);
                          setIssue(null);
                        }}
                      >
                        {c.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>

      {data.error && <ErrorBanner message={data.error} />}
      {company && !d && !data.error && <Loading label="Reading tickets, time, hardware, projects and invoices from Autotask" />}

      {d && (
        <>
          <p className="xs muted mb-2">
            {d.company.name} · {monthLabel(d.month)} · read{' '}
            {new Date(d.fetchedUtc).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </p>
          {d.notes.length > 0 && (
            <div className="card mb-4 pf-warn">
              <div className="card-body small">
                {d.notes.map((n) => (
                  <p key={n} style={{ margin: '0 0 4px' }}>
                    ⚠ {n}
                  </p>
                ))}
              </div>
            </div>
          )}

          <Section title="At a glance">
            <div className="grid grid-3 mb-4">
              <Stat label="1. Tickets opened" value={d.summary.opened} foot={`${d.alerts.total} of them automated alerts`} />
              <Stat label="2. Tickets closed" value={d.summary.closed} foot={`${d.openTickets.length} open today`} />
              <Stat
                label="3. Average time to resolve"
                value={d.summary.avgResolveHours === null ? '—' : d.summary.avgResolveHours < 48 ? `${num(d.summary.avgResolveHours, 1)} h` : `${num(d.summary.avgResolveHours / 24, 1)} days`}
                foot="from opened to completed, closed tickets"
              />
              <Stat
                label="4. First response on time"
                value={d.summary.firstResponse.pct === null ? '—' : `${num(d.summary.firstResponse.pct, 0)}%`}
                foot={`${d.summary.firstResponse.onTime} of ${d.summary.firstResponse.measured} tickets with a response target`}
              />
              <Stat label="5. Labor used" value={hrs(d.summary.hoursWorked)} foot={`${hrs(d.labor.supportHours)} support · ${hrs(d.labor.projectHours)} projects`} />
              <Stat label="6. Invoiced" value={money(d.summary.invoiced)} foot={`${d.summary.invoices} invoice${d.summary.invoices === 1 ? '' : 's'}, with tax`} />
            </div>
          </Section>

          <Section title="Service desk">
            <div className="pf-split mb-4">
              <ChartCard
                title={drilled ? `7. Sub-issues of ${drilled.label}` : '7. Ticket types by issue'}
                subtitle={drilled ? 'Sub-issue types within the issue chosen.' : 'Issue type of each ticket opened. Click an issue for its sub-issues.'}
                actions={
                  drilled && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setIssue(null)}>
                      ← All issues
                    </button>
                  )
                }
                table={{
                  columns: [
                    { key: 'issue', label: 'Issue type' },
                    { key: 'sub', label: 'Sub-issue type' },
                    { key: 'count', label: 'Tickets', align: 'right' },
                  ],
                  rows: (drilled ? [drilled] : d.issues).flatMap((i) => i.subIssues.map((s) => ({ issue: i.label, sub: s.label, count: s.count }))),
                }}
              >
                {drilled ? (
                  <DonutChart slices={drilled.subIssues.map((s) => ({ label: s.label, value: s.count }))} unit="tickets" />
                ) : (
                  <DonutChart
                    slices={d.issues.map((i) => ({ label: i.label, value: i.count }))}
                    unit="tickets"
                    emptyMessage="No tickets opened this month."
                    onSelect={(s) => setIssue(s.label)}
                    selected={issue}
                  />
                )}
              </ChartCard>

              <ChartCard
                title="8. Tickets by employee"
                subtitle="Who at the client raised each ticket — the ticket's contact."
                table={{
                  columns: [
                    { key: 'name', label: 'Employee' },
                    { key: 'email', label: 'Email', render: (r) => r.email ?? '—' },
                    { key: 'tickets', label: 'Tickets', align: 'right' },
                    { key: 'open', label: 'Still open', align: 'right' },
                  ],
                  rows: d.byEmployee,
                }}
              >
                <RankedBars rows={d.byEmployee.slice(0, 12).map((e) => ({ label: e.name, value: e.tickets, meta: e.email ?? undefined }))} emptyMessage="No tickets opened this month." />
                {d.byEmployee.length > 12 && <p className="sub mt-2">The top 12 of {d.byEmployee.length}; the table has everyone.</p>}
              </ChartCard>
            </div>

            <div className="pf-split mb-4">
              <ChartCard title="9. Tickets by priority" table={{ columns: countCols('Priority'), rows: d.byPriority }}>
                <RankedBars rows={d.byPriority.map((p) => ({ label: p.label, value: p.count }))} emptyMessage="No tickets opened this month." />
              </ChartCard>
              <ChartCard title="10. How tickets came in" subtitle="The ticket's source in Autotask." table={{ columns: countCols('Source'), rows: d.bySource }}>
                <DonutChart slices={d.bySource.map((s) => ({ label: s.label, value: s.count }))} unit="tickets" size={180} emptyMessage="No tickets opened this month." />
              </ChartCard>
            </div>

            <ChartCard
              className="mb-4"
              title="11. Tickets by day"
              subtitle="Tickets opened each day, people's requests and automated alerts stacked."
              table={{
                columns: [
                  { key: 'day', label: 'Day', render: (r) => date(r.day) },
                  { key: 'tickets', label: 'Requests', align: 'right' },
                  { key: 'alerts', label: 'Alerts', align: 'right' },
                ],
                rows: d.perDay,
              }}
            >
              <BarChart
                data={d.perDay}
                xKey="day"
                stacked
                series={[
                  { key: 'tickets', label: 'Requests', color: 'var(--series-1)' },
                  { key: 'alerts', label: 'Alerts', color: 'var(--series-2)' },
                ]}
                xFormat={(v) => String(Number(v.slice(8, 10)))}
              />
              <div className="chart-legend">
                <span className="chart-legend-item">
                  <span className="chart-legend-swatch" style={{ background: 'var(--series-1)' }} />
                  Requests
                </span>
                <span className="chart-legend-item">
                  <span className="chart-legend-swatch" style={{ background: 'var(--series-2)' }} />
                  Alerts
                </span>
              </div>
            </ChartCard>

            <Card className="mb-4" title={`12. Open tickets (${d.openTickets.length})`} subtitle="Everything not yet complete for this client, today, oldest first.">
              <DataTable
                rows={d.openTickets}
                rowKey={(r) => String(r.id)}
                empty="No open tickets."
                columns={[
                  { key: 'ticketNumber', label: 'Ticket', render: ticketLink },
                  { key: 'title', label: 'Title', render: (r) => r.title ?? '—' },
                  { key: 'contact', label: 'Raised by', render: (r) => r.contact ?? '—' },
                  { key: 'status', label: 'Status', render: (r) => r.status ?? '—' },
                  { key: 'priority', label: 'Priority', render: (r) => r.priority ?? '—' },
                  { key: 'ageDays', label: 'Age (days)', align: 'right', render: (r) => (r.ageDays === null ? '—' : String(r.ageDays)) },
                ]}
              />
            </Card>

            <Card className="mb-4" title="13. Recurring issues" subtitle="A sub-issue raised three times or more this month — worth a permanent fix.">
              <DataTable
                rows={d.recurring}
                empty="Nothing came up three times or more."
                columns={[
                  { key: 'issue', label: 'Issue type' },
                  { key: 'subIssue', label: 'Sub-issue type' },
                  { key: 'count', label: 'Tickets', align: 'right' },
                ]}
              />
            </Card>
          </Section>

          <Section title="Monitoring and security">
            <div className="pf-split mb-4">
              <ChartCard
                title="14. Alerts by type"
                subtitle={`${d.alerts.total} automated alert${d.alerts.total === 1 ? '' : 's'}, by the NOC alert library's categories.`}
                table={{ columns: countCols('Alert type'), rows: d.alerts.byType }}
              >
                <RankedBars rows={d.alerts.byType.map((a) => ({ label: a.label, value: a.count }))} emptyMessage="No automated alerts this month." />
              </ChartCard>
              <ChartCard
                title="15. Security alerts by week"
                subtitle={`${d.security.list.length} security alert${d.security.list.length === 1 ? '' : 's'} and security tickets.`}
                table={{
                  columns: [
                    { key: 'label', label: 'Days of the month' },
                    { key: 'alerts', label: 'Security alerts', align: 'right' },
                  ],
                  rows: d.security.weeks,
                }}
              >
                <BarChart data={d.security.weeks} xKey="label" series={[{ key: 'alerts', label: 'Security alerts', color: 'var(--series-2)' }]} height={200} />
              </ChartCard>
            </div>

            <Card className="mb-4" title="16. Security alerts" subtitle="Each one, with what the alert said.">
              <DataTable
                rows={d.security.list}
                rowKey={(r) => String(r.id)}
                empty="No security alerts this month."
                columns={[
                  { key: 'ticketNumber', label: 'Ticket', render: ticketLink },
                  { key: 'created', label: 'Date', render: (r) => date(r.created) },
                  { key: 'title', label: 'Alert', render: (r) => <strong>{r.title ?? '—'}</strong> },
                  { key: 'description', label: 'Description', render: (r) => <span className="xs cr-desc">{r.description ?? '—'}</span> },
                  {
                    key: 'status',
                    label: 'Status',
                    render: (r) => (r.completed ? <StatusChip band="good">Resolved</StatusChip> : <StatusChip band="serious">{r.status ?? 'Open'}</StatusChip>),
                  },
                ]}
              />
            </Card>
          </Section>

          <Section title="Labor">
            <div className="pf-split mb-4">
              <ChartCard
                title="17. Labor by technician"
                table={{
                  columns: [
                    { key: 'label', label: 'Technician' },
                    { key: 'hours', label: 'Hours', align: 'right', render: (r: Labor) => num(r.hours, 2) },
                    { key: 'billable', label: 'Billable', align: 'right', render: (r: Labor) => num(r.billable, 2) },
                  ],
                  rows: d.labor.byTechnician,
                }}
              >
                <RankedBars rows={d.labor.byTechnician.map((l) => ({ label: l.label, value: l.hours }))} format={hrs} emptyMessage="No time logged this month." />
              </ChartCard>
              <ChartCard
                title="18. Labor by work type"
                table={{
                  columns: [
                    { key: 'label', label: 'Work type' },
                    { key: 'hours', label: 'Hours', align: 'right', render: (r: Labor) => num(r.hours, 2) },
                    { key: 'billable', label: 'Billable', align: 'right', render: (r: Labor) => num(r.billable, 2) },
                  ],
                  rows: d.labor.byWorkType,
                }}
              >
                <RankedBars rows={d.labor.byWorkType.map((l) => ({ label: l.label, value: l.hours }))} format={hrs} emptyMessage="No time logged this month." />
              </ChartCard>
            </div>
            <Card className="mb-4" title="19. Where the hours went" subtitle="Support tickets against project work, and what was billed against what the agreement covered.">
              <div className="grid grid-4">
                <Stat label="Support tickets" value={hrs(d.labor.supportHours)} />
                <Stat label="Projects" value={hrs(d.labor.projectHours)} />
                <Stat label="Billable" value={hrs(d.labor.billableHours)} />
                <Stat label="Covered / not billed" value={hrs(d.labor.notBilledHours)} />
              </div>
            </Card>
          </Section>

          <Section title="Hardware">
            <Card className="mb-4" title={`20. Active hardware (${d.hardware.list.length})`} subtitle="Every active configuration item on the account.">
              <DataTable
                rows={d.hardware.list}
                rowKey={(r) => String(r.id)}
                empty="No active configuration items."
                columns={[
                  { key: 'name', label: 'Device' },
                  { key: 'category', label: 'Type', render: (r) => r.category ?? r.type ?? '—' },
                  { key: 'product', label: 'Product', render: (r) => r.product ?? '—' },
                  { key: 'serialNumber', label: 'Serial', render: (r) => r.serialNumber ?? '—' },
                  { key: 'location', label: 'Location', render: (r) => r.location ?? '—' },
                  { key: 'installDate', label: 'Installed', render: (r) => date(r.installDate) },
                  { key: 'warrantyExpiration', label: 'Warranty', render: (r) => date(r.warrantyExpiration) },
                ]}
              />
            </Card>
            <div className="pf-split mb-4">
              <ChartCard title="21. Hardware by type" table={{ columns: [{ key: 'label', label: 'Type' }, { key: 'count', label: 'Devices', align: 'right' }], rows: d.hardware.byType }}>
                <RankedBars rows={d.hardware.byType.map((t) => ({ label: t.label, value: t.count }))} emptyMessage="No active configuration items." />
              </ChartCard>
              <Card title="22. Warranties" subtitle={`Expired, or expiring in the next 90 days.${d.hardware.unknownWarranty ? ` ${d.hardware.unknownWarranty} devices have no warranty date.` : ''}`}>
                <DataTable
                  rows={d.hardware.warranties}
                  rowKey={(r) => String(r.id)}
                  empty="Nothing expired or expiring soon."
                  columns={[
                    { key: 'name', label: 'Device' },
                    { key: 'warrantyExpiration', label: 'Expires', render: (r) => date(r.warrantyExpiration) },
                    {
                      key: 'warranty',
                      label: 'Status',
                      render: (r) => (r.warranty === 'expired' ? <StatusChip band="critical">Expired</StatusChip> : <StatusChip band="warning">Expiring</StatusChip>),
                    },
                  ]}
                />
              </Card>
            </div>
            <ChartCard
              className="mb-4"
              title="23. Devices with the most tickets"
              subtitle="Tickets opened this month naming the device — candidates for repair or replacement."
              table={{
                columns: [
                  { key: 'name', label: 'Device' },
                  { key: 'category', label: 'Type', render: (r: Device) => r.category ?? r.type ?? '—' },
                  { key: 'ticketsThisMonth', label: 'Tickets', align: 'right' },
                ],
                rows: d.hardware.mostTickets,
              }}
            >
              <RankedBars rows={d.hardware.mostTickets.map((h) => ({ label: h.name, value: h.ticketsThisMonth }))} emptyMessage="No ticket named a device this month." />
            </ChartCard>
          </Section>

          <Section title="Projects and billing">
            <Card className="mb-4" title={`24. Open projects (${d.projects.length})`}>
              <DataTable
                rows={d.projects}
                rowKey={(r) => String(r.id)}
                empty="No open projects."
                columns={[
                  { key: 'name', label: 'Project' },
                  { key: 'status', label: 'Status', render: (r) => r.status ?? '—' },
                  { key: 'lead', label: 'Lead', render: (r) => r.lead ?? '—' },
                  { key: 'start', label: 'Started', render: (r) => date(r.start) },
                  { key: 'due', label: 'Due', render: (r) => (r.overdue ? <StatusChip band="serious">{date(r.due)}</StatusChip> : date(r.due)) },
                  { key: 'percentComplete', label: 'Complete', align: 'right', render: (r) => (r.percentComplete === null ? '—' : `${num(r.percentComplete, 0)}%`) },
                  { key: 'hours', label: 'Hours used / est.', align: 'right', render: (r) => `${num(r.actualHours, 1)} / ${num(r.estimatedHours, 1)}` },
                ]}
              />
            </Card>
            <Card
              className="mb-4"
              title="25. Invoice items"
              subtitle={`${d.invoices.lines} line${d.invoices.lines === 1 ? '' : 's'} on ${d.invoices.list.length} invoice${d.invoices.list.length === 1 ? '' : 's'} (${d.invoices.list.map((i) => `#${i.number}`).join(', ') || 'none'}), grouped by item.`}
            >
              <DataTable
                rows={d.invoices.items}
                rowKey={(r) => r.item}
                empty="No invoice lines this month."
                columns={[
                  { key: 'item', label: 'Item' },
                  { key: 'lines', label: 'Lines', align: 'right' },
                  { key: 'quantity', label: 'Quantity', align: 'right', render: (r) => num(r.quantity, 2) },
                  { key: 'amount', label: 'Amount', align: 'right', render: (r) => money(r.amount) },
                ]}
              />
            </Card>
          </Section>
        </>
      )}
    </>
  );
}
